import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Skeleton, Typography } from '@mui/material';
import { SplitTabsPanel } from '@/components/common';
import { BalanceSheet, ProfitLoss } from '@/components/fs-report';
import { BalanceSheetOutlinedIcon, ProfitLossOutlinedIcon } from '@/assets/icons/FsStatementIcon';
import { useFsCategoryStore } from '@/store/audit/fsCategoryStore';
import { useGlInvoiceCreateCtx } from '@/pages/components/account/gl-invoice/create-stepper/context';
import {
    fsLastYearMapsToPrior,
    mergePriorYearReplaceByFs,
    mergeRemarksReplaceByFs,
    preservePriorCotIfHarvestWiped,
    preservePriorGlSplit,
    priorToFsLastYearMaps,
} from '@/pages/components/account/gl-invoice/glLastYearFs';
import {
    GlLastYearBalanceAlert,
    type GlLastYearBsStatus,
} from '@/pages/components/account/gl-invoice/GlLastYearBalanceAlert';
import PriorYearFsExtractBar, {
    type PriorYearPeriodHint,
} from '@/pages/components/account/gl-invoice/PriorYearFsExtractBar';
import { getAnnual } from '@/services/accountService/companyAnnualService';

const PANEL_HEIGHT = 'calc(85vh - 220px)';

/**
 * Create GL：发票步骤之前，用与 FS 相同的资产负债表 / 损益表录入上年数。
 */
export default function StepLastYear() {
    const { state, dispatch } = useGlInvoiceCreateCtx();
    const companyId = state.companyId ?? 0;
    const annualId = state.annualId ?? 0;
    const [expectedPeriod, setExpectedPeriod] = useState<PriorYearPeriodHint | undefined>();

    const {
        loading: sLoading,
        error: sError,
        initIfNeeded,
        byCode,
        bsTree,
        plTree,
        setBsLastYearValues,
        setPlLastYearValues,
        setBsRetainedEarningsUseStored,
        setGlSuppressPlAfterTaxLastYear,
        setPLComputed,
    } = useFsCategoryStore();

    const snapshotRef = useRef<{
        bsLast: Record<number, string>;
        plLast: Record<number, string>;
        useStored: boolean;
        suppress: boolean;
        afterTaxLastYear: number | null;
    } | null>(null);
    const [ready, setReady] = useState(false);
    const [tab, setTab] = useState('BS');
    const [bsError, setBsError] = useState<string | null>(null);
    const [bsStatus, setBsStatus] = useState<GlLastYearBsStatus | null>(null);
    /** Apply 抽取结果后递增，强制 BS/PL 重挂载以读入 store 上年数 */
    const [editorSeed, setEditorSeed] = useState(0);

    useEffect(() => {
        if (!companyId || !annualId) return;
        initIfNeeded(companyId, annualId);
    }, [companyId, annualId, initIfNeeded]);

    useEffect(() => {
        let cancelled = false;
        const fromImport = state.contentData;
        if (fromImport?.lastYearPeriodFrom || fromImport?.lastYearPeriodTo) {
            setExpectedPeriod({
                expectedFrom: fromImport.lastYearPeriodFrom ?? null,
                expectedTo: fromImport.lastYearPeriodTo ?? null,
                expectedLabel: `${fromImport.lastYearPeriodFrom || '?'} ~ ${fromImport.lastYearPeriodTo || '?'}`,
            });
            return;
        }
        if (!annualId) {
            setExpectedPeriod(undefined);
            return;
        }
        getAnnual(annualId)
            .then((a) => {
                if (cancelled || !a) return;
                const from = a.lastYearPeriodFrom || null;
                const to = a.lastYearPeriodTo || null;
                setExpectedPeriod({
                    expectedFrom: from,
                    expectedTo: to,
                    expectedLabel: from || to ? `${from || '?'} ~ ${to || '?'}` : null,
                });
            })
            .catch(() => {
                if (!cancelled) setExpectedPeriod(undefined);
            });
        return () => { cancelled = true; };
    }, [annualId, state.contentData]);

    const byCodeReady = Object.keys(byCode).length > 0;
    /** 占位 key 未 remap 时灌表会全 0；等正式 fs|gl 后再灌一次 */
    const priorKeyFingerprint = useMemo(() => {
        const keys = Object.keys(state.summaryPriorYear || {}).sort();
        const hasPlaceholder = keys.some((k) => k.includes('_UNM_'));
        return `${hasPlaceholder ? 'P' : 'R'}:${keys.length}`;
    }, [state.summaryPriorYear]);
    const appliedPriorFpRef = useRef<string | null>(null);
    const summaryPriorRef = useRef(state.summaryPriorYear);
    summaryPriorRef.current = state.summaryPriorYear;

    useEffect(() => {
        if (!companyId || !annualId || sLoading || !byCodeReady) return;
        // Mapping 未确认时 prior 仍是 _UNM_，灌表会全 0；等确认后再灌
        if (priorKeyFingerprint.startsWith('P:') && state.pendingLegacyMapping) return;
        if (appliedPriorFpRef.current === priorKeyFingerprint) return;

        const store = useFsCategoryStore.getState();
        if (!snapshotRef.current) {
            snapshotRef.current = {
                bsLast: { ...store.bsLastYearValues },
                plLast: { ...store.plLastYearValues },
                useStored: store.bsRetainedEarningsUseStored,
                suppress: store.glSuppressPlAfterTaxLastYear,
                afterTaxLastYear: store.plAfterTaxLastYear,
            };
        }
        const { bs, pl, lfpLastYear } = priorToFsLastYearMaps(
            state.summaryPriorYear,
            state.preloadedColumnDefs ?? [],
            store.byCode,
            { plTree: store.plTree, bsTree: store.bsTree },
        );
        setBsLastYearValues(bs);
        setPlLastYearValues(pl);
        setBsRetainedEarningsUseStored(true);
        setGlSuppressPlAfterTaxLastYear(false);
        setPLComputed({ afterTaxLastYear: lfpLastYear });
        const isReapply = appliedPriorFpRef.current != null;
        appliedPriorFpRef.current = priorKeyFingerprint;
        setReady(true);
        // 首次灌表靠 ready 挂载编辑器即可；仅 remap / 再灌时强制重挂载
        if (isReapply) setEditorSeed((n) => n + 1);
    }, [
        companyId, annualId, sLoading, byCodeReady,
        state.summaryPriorYear, state.preloadedColumnDefs, state.pendingLegacyMapping,
        priorKeyFingerprint,
        setBsLastYearValues, setPlLastYearValues,
        setBsRetainedEarningsUseStored, setGlSuppressPlAfterTaxLastYear, setPLComputed,
    ]);

    const harvest = useCallback(async () => {
        const store = useFsCategoryStore.getState();
        const harvested = fsLastYearMapsToPrior(
            store.bsLastYearValues,
            store.plLastYearValues,
            store.byCode,
            { plAfterTaxLastYear: store.plAfterTaxLastYear },
        );
        const withCot = preservePriorCotIfHarvestWiped(harvested, summaryPriorRef.current || {});
        // FS 合并编辑；落库保留 Sales / Refund 等 GL 分列
        dispatch({
            type: 'set_summary_prior_year',
            payload: preservePriorGlSplit(withCot, summaryPriorRef.current || {}),
        });
        return true;
    }, [dispatch]);

    // harvest 用 ref 注册，避免 summaryPriorYear 变化时 cleanup 把上年数还原成空快照
    useEffect(() => {
        dispatch({ type: 'set_before_next', payload: harvest });
        return () => {
            dispatch({ type: 'set_before_next', payload: undefined });
            const snap = snapshotRef.current;
            if (!snap) return;
            const store = useFsCategoryStore.getState();
            store.setBsLastYearValues(snap.bsLast);
            store.setPlLastYearValues(snap.plLast);
            store.setBsRetainedEarningsUseStored(snap.useStored);
            store.setGlSuppressPlAfterTaxLastYear(snap.suppress);
            store.setPLComputed({ afterTaxLastYear: snap.afterTaxLastYear });
        };
    }, [dispatch, harvest]);

    const handleBsErrorChange = useCallback((msg: string | null) => {
        setBsError((prev) => (prev === (msg || null) ? prev : (msg || null)));
    }, []);
    const handleBsStatusChange = useCallback((s: GlLastYearBsStatus) => {
        setBsStatus((prev) => {
            if (
                prev
                && prev.netAssets === s.netAssets
                && prev.capitalReserves === s.capitalReserves
                && prev.equal === s.equal
                && prev.zero === s.zero
                && prev.lastYearNetAssets === s.lastYearNetAssets
                && prev.lastYearCapitalReserves === s.lastYearCapitalReserves
                && prev.equalLastYear === s.equalLastYear
                && prev.zeroLastYear === s.zeroLastYear
            ) {
                return prev;
            }
            return s;
        });
    }, []);

    const [remarksByFsCode, setRemarksByFsCode] = useState<Record<string, string>>(
        () => state.summaryPriorYearRemarks || {},
    );

    useEffect(() => {
        dispatch({ type: 'set_summary_prior_year_remarks', payload: remarksByFsCode });
    }, [dispatch, remarksByFsCode]);

    /** 抽取结果按 FS 覆盖进 summaryPriorYear（同 FS 旧 key 清掉，避免翻倍），并重灌 BS/PL */
    const handleExtractApply = useCallback((
        extracted: Record<string, number>,
        _result?: unknown,
        remarks?: Record<string, string>,
    ) => {
        const normalized: Record<string, number> = {};
        for (const [k, v] of Object.entries(extracted || {})) {
            const n = Number(v);
            if (k && Number.isFinite(n)) normalized[k] = n;
        }
        const touchedFs = new Set<string>();
        for (const k of Object.keys(normalized)) {
            const i = k.indexOf('|');
            if (i > 0) touchedFs.add(k.slice(0, i));
        }
        for (const fs of Object.keys(remarks || {})) {
            if (fs) touchedFs.add(fs);
        }
        const merged = mergePriorYearReplaceByFs(state.summaryPriorYear, normalized);
        dispatch({ type: 'set_summary_prior_year', payload: merged });
        setRemarksByFsCode((prev) => mergeRemarksReplaceByFs(prev, remarks, touchedFs));
        if (!byCodeReady) return;
        const store = useFsCategoryStore.getState();
        const { bs: bsMap, pl: plMap, lfpLastYear } = priorToFsLastYearMaps(
            merged,
            state.preloadedColumnDefs ?? [],
            store.byCode,
            { plTree: store.plTree, bsTree: store.bsTree },
        );
        // 只覆盖本次触达的 FS 行；其它手工行保留
        setBsLastYearValues({ ...(store.bsLastYearValues || {}), ...bsMap });
        setPlLastYearValues({ ...(store.plLastYearValues || {}), ...plMap });
        setBsRetainedEarningsUseStored(true);
        setGlSuppressPlAfterTaxLastYear(false);
        setPLComputed({ afterTaxLastYear: lfpLastYear });
        // BS/PL 用本地 lastYearAmounts，需重挂载才能从 store 读入
        setEditorSeed((n) => n + 1);
    }, [
        dispatch, state.summaryPriorYear, state.preloadedColumnDefs, byCodeReady,
        setBsLastYearValues, setPlLastYearValues,
        setBsRetainedEarningsUseStored, setGlSuppressPlAfterTaxLastYear, setPLComputed,
    ]);

    const bs = useMemo(() => (
        <BalanceSheet
            key={`ly-bs-${editorSeed}`}
            mode="fs"
            annualId={annualId}
            lastYearOnly
            lastYearRemarkByFsCode={remarksByFsCode}
            onErrorChange={handleBsErrorChange}
            onStatusChange={handleBsStatusChange}
        />
    ), [annualId, editorSeed, remarksByFsCode, handleBsErrorChange, handleBsStatusChange]);
    const pl = useMemo(() => (
        <ProfitLoss
            key={`ly-pl-${editorSeed}`}
            mode="fs"
            annualId={annualId}
            lastYearOnly
            lastYearRemarkByFsCode={remarksByFsCode}
        />
    ), [annualId, editorSeed, remarksByFsCode]);

    const tabs = useMemo(() => ([
        { key: 'BS', label: 'Balance Sheet', icon: <BalanceSheetOutlinedIcon sx={{ fontSize: 24 }} />, content: bs },
        { key: 'PL', label: 'Income Statement', icon: <ProfitLossOutlinedIcon sx={{ fontSize: 24 }} />, content: pl },
    ]), [bs, pl]);

    if (!companyId || !annualId) {
        return <Alert severity="info">Select a company and annual first.</Alert>;
    }

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={700}>Last Year</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enter comparative figures the same way as the financial statements. Current year stays on the next step.
            </Typography>
            <PriorYearFsExtractBar
                onApply={handleExtractApply}
                disabled={!ready || !!sError}
                expectedPeriod={expectedPeriod}
                companyId={companyId}
            />
            {sError && <Alert severity="error" sx={{ mb: 1 }}>{sError}</Alert>}
            {ready && !sError && <GlLastYearBalanceAlert errorMsg={bsError} status={bsStatus} />}
            {(sLoading || !ready) && (
                <Box sx={{ p: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)}
                </Box>
            )}
            {ready && !sError && ((bsTree?.length ?? 0) > 0 || (plTree?.length ?? 0) > 0) && (
                <SplitTabsPanel
                    tabs={tabs}
                    height={PANEL_HEIGHT}
                    selectedKey={tab}
                    onChange={setTab}
                    renderAll
                    allowMaximize={false}
                />
            )}
        </Box>
    );
}
